import React, { FC, useCallback, useContext, useMemo } from 'react';
import { EOrganizationRole, organizationRoles } from '../../const/roles';
import Chip from '@mui/material/Chip';
import { UUID } from '../../types/uuid';
import { apiOrganization } from '../../resources/api/apiOrganization';
import { IBasicUserInfo } from '../../resources/models/IUserInfo';
import { OrganizationContext } from '../../contexts/OrganizationContext';
import { useResourceChecker } from '../../resources/resource';
import { Edit } from '@mui/icons-material';
import { useHistory } from 'react-router-dom';
import { useDialog } from '../../hooks/useDialog';
import { useTranslation } from 'react-i18next';

interface IProps {
  user: IBasicUserInfo & { roles: UUID[]; owner: boolean };
}

const { useUpdateMemberRole } = apiOrganization;

export const UserRoleInOrganizationChip: FC<IProps> = ({ user }) => {
  const { t } = useTranslation();
  const { organization } = useContext(OrganizationContext);
  const { resource: resourceUpdate } = useUpdateMemberRole(organization?.name, user.id, user.roles[0]);
  const resourceChecker = useResourceChecker();
  const history = useHistory();
  const { openUrl } = useDialog(user.id, 'change-role');

  const mappedRoles = useMemo(
    () => user.roles.map((role) => organizationRoles.find((orgRole) => orgRole.id === role)?.role),
    [user.roles]
  );

  const onEdit = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
      history.push(openUrl);
    },
    [openUrl]
  );

  const canEdit = !user.owner && resourceChecker(resourceUpdate);

  return (
    <Chip
      color={'default'}
      label={
        user.owner
          ? t('roles.organization.owner')
          : mappedRoles.includes(EOrganizationRole.Admin)
          ? t('roles.organization.admin')
          : t('roles.organization.member')
      }
      // It's actually Edit, not Delete action, but it was just convenient to use `deleteIcon` and `onDelete`
      deleteIcon={canEdit ? <Edit /> : void 0}
      onDelete={canEdit ? onEdit : void 0}
      clickable={canEdit}
      onClick={canEdit ? onEdit : void 0}
      component={'div'}
    />
  );
};
